import React, { useState, useEffect } from "react";
import ReactGA from "react-ga4";
import Button from "../components/Button";
import VerificationCode from "../components/VerificationCode";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useStateContext } from "../contexts/ContextProvider";
import { FaChevronLeft } from "react-icons/fa";
import heroSmall from "../data/images/hero-small.png";
import {
  createUser,
  getAuthenticatedUser,
  loginUser,
} from "../services/apiService";
import HeadMetaTags from "../components/HeadMetaTags";

const initialErrorState = {
  firstName: "",
  lastName: "",
  email: "",
  requestError: "",
};

const Signup = () => {
  const { signup, setNavigateToDashboard } = useStateContext();
  const [showBackButton, setShowBackButton] = useState(true);
  const [showSignup, setShowSignup] = useState(true);
  const [requestCode, setRequestCode] = useState(false);
  const [error, setError] = useState(initialErrorState);
  const [apiError, setApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    defaultView: "",
  });

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const action = searchParams.get("action");

  const isTracking =
    process.env.REACT_APP_GA_TRACKING === "true" ? true : false;

  useEffect(() => {
    if (action === "post") {
      handleUserType("landlord");
      setShowBackButton(false);
    }
    if (isTracking) {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname + location.search,
      });
    }
    // eslint-disable-next-line
  }, [location]);

  const handleGAEvent = (category, action, label) => {
    if (isTracking) {
      ReactGA.event({
        category,
        action,
        label,
      });
    }
  };

  const handleUserType = (type) => {
    setNewUser((prevState) => ({ ...prevState, defaultView: type }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setNewUser((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleBack = () => {
    handleUserType("");
    setShowBackButton(false);
    setApiError(false);
    setShowSignup(true);
    setRequestCode(false);
    setNavigateToDashboard(true);
    resetEmail();
  };

  const resetEmail = () => {
    setNewUser((prevState) => ({ ...prevState, email: "" }));
    setErrorMessage("");
  };

  const handleSignup = async (code) => {
    setNavigateToDashboard(false);
    const payload = {
      email: newUser.email,
      password:
        code.code1 +
        code.code2 +
        code.code3 +
        code.code4 +
        code.code5 +
        code.code6,
    };
    if (hasErrors()) {
      return;
    } else {
      const response = await loginUser(payload);
      if (response.hasError) {
        setApiError(true);
        setErrorMessage(response.error.response.data.message);
        activateError("requestError");
        return;
      } else {
        handleGAEvent("Form Submit", "Success", "New User Logged In");
        const response = await getAuthenticatedUser();
        signup(response.data.user);
        navigate("/profile");
        setNewUser({});
      }
    }
  };

  const handleContinue = async () => {
    if (hasErrors()) {
      return;
    } else {
      const response = await createUser(newUser);
      if (response.hasError) {
        setApiError(true);
        setErrorMessage(response.error.response.data.errors[0].message);
        activateError("requestError");
        return;
      } else {
        handleGAEvent("Form Submit", "Success", "New User Created");
        if (apiError) setApiError(false);
        setShowSignup(!showSignup);
        setRequestCode(!requestCode);
      }
    }
  };

  const hasErrors = () => {
    const reg = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/); // valid email format?
    if (newUser.firstName === "") {
      activateError("firstName");
      return true;
    } else if (newUser.lastName === "") {
      activateError("lastName");
      return true;
    } else if (newUser.email === "" || !reg.test(newUser.email)) {
      activateError("email");
      return true;
    } else if (error.requestError !== "") {
      return true;
    } else {
      return false;
    }
  };

  const activateError = (item) => {
    setError((prevState) => ({
      ...prevState,
      [item]: "animate-shake text-red-600 border-red-600 border-2",
    }));
    setTimeout(() => {
      setError((prevState) => ({
        ...prevState,
        [item]: "",
      }));
    }, 500);
    clearTimeout();
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-white z-50">
      <HeadMetaTags
        title="Signup | Roommates Made Simple"
        ogTitle="Signup | Roommates Made Simple"
      />
      <div className="pl-3 border-gray-300 border-b-1">
        <NavLink
          id="logo"
          to="/"
          className="text-snappily-orange text-center text-3xl"
        >
          <div className="flex items-center">
            <img
              src={heroSmall}
              alt="Snappily Logo"
              className="h-20 w-40 object-contain ml-1"
            />
          </div>
        </NavLink>
      </div>
      {newUser.defaultView !== "" && showBackButton && (
        <div
          className="flex items-center  h-10 bg-snappily-orange border-gray-300 
            border-t-1 text-xl text-white p-3 gap-x-3 mt-0 cursor-pointer z-20"
          onClick={() => {
            handleBack();
            handleGAEvent("Button Click", "Clicked", "Cancel Signup");
          }}
        >
          <FaChevronLeft />
          Back
        </div>
      )}
      {newUser.defaultView === "" && (
        <div className="grid justify-items-center mt-14">
          <div className="flex justify-center text-2xl mt-10">
            What would you like to do?
          </div>
          <div className="mt-10">
            <Button
              background="bg-snappily-orange"
              text="text-white"
              opacity="bg-opacity-100"
              title="I have a room for rent"
              height="h-10"
              width="w-72"
              borderHover="hover:border-snappily-orange"
              customFunc={() => {
                handleUserType("landlord");
                handleGAEvent(
                  "Button Clicked",
                  "Clicked",
                  "I have a room for rent"
                );
                setShowBackButton(true);
              }}
            />
          </div>
          <div className="mt-5">
            <Button
              background="bg-snappily-orange"
              text="text-white"
              opacity="bg-opacity-100"
              title="I need a room"
              height="h-10"
              width="w-72"
              borderHover="hover:border-snappily-orange"
              customFunc={() => {
                handleUserType("tenant");
                handleGAEvent("Button Click", "Clicked", "I need a room");
                setShowBackButton(true);
              }}
            />
          </div>
        </div>
      )}
      {((newUser.defaultView !== "" && showSignup) || apiError) && (
        <>
          <div className="flex justify-center text-2xl mt-14">
            Create Your Account
          </div>
          <div className="grid justify-items-center mt-5">
            <input
              id="first-name"
              name="firstName"
              type="text"
              required
              placeholder="First Name"
              autoComplete="given-name"
              value={newUser.firstName}
              onChange={handleChange}
              className={`h-10 w-72 border-gray-300 rounded-xl text-lg border-1 px-5 mt-5 ${error.firstName}`}
            />
            <input
              id="last-name"
              name="lastName"
              type="text"
              required
              placeholder="Last Name"
              autoComplete="family-name"
              value={newUser.lastName}
              onChange={handleChange}
              className={`h-10 w-72 border-gray-300 rounded-xl text-lg border-1 px-5 mt-2 ${error.lastName}`}
            />
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Email Address"
              autoComplete="email"
              onChange={handleChange}
              onClick={apiError ? resetEmail : () => {}}
              value={newUser.email}
              className={`h-10 w-72 border-gray-300 rounded-xl text-lg border-1 px-5 mt-2 ${error.email}`}
            />
          </div>
          <div className="flex justify-center mt-5">
            <Button
              background="bg-snappily-orange"
              text="text-white"
              opacity="bg-opacity-100"
              title="Continue"
              height="h-10"
              width="w-72"
              borderHover="hover:border-snappily-orange"
              customFunc={() => {
                handleContinue();
                handleGAEvent("Clicked", "Create Account");
              }}
            />
          </div>

          <div className="flex justify-center text-sm text-gray-400 mt-2">
            Already have an Account?{" "}
            <span
              className="text-snappily-orange cursor-pointer"
              onClick={() => {
                navigate("/login");
                handleGAEvent("Clicked", "Already Have Account");
              }}
            >
              &nbsp; Log in Here
            </span>
          </div>
          {apiError && (
            <div className="flex justify-center h-10 mt-2 text-red-600">
              {errorMessage}
            </div>
          )}
        </>
      )}
      {requestCode && !apiError && (
        <VerificationCode
          email={newUser.email}
          type="signup"
          customFunc={handleSignup}
        />
      )}
    </div>
  );
};

export default Signup;
